import type * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Badge from '@cloudscape-design/components/badge';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ButtonDropdown from '@cloudscape-design/components/button-dropdown';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Container from '@cloudscape-design/components/container';
import ContentLayout from '@cloudscape-design/components/content-layout';
import DatePicker from '@cloudscape-design/components/date-picker';
import Header from '@cloudscape-design/components/header';
import Input from '@cloudscape-design/components/input';
import Modal from '@cloudscape-design/components/modal';
import Pagination from '@cloudscape-design/components/pagination';
import Select, { type SelectProps } from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Table from '@cloudscape-design/components/table';
import type {
  FinanceAccount,
  FinanceEntry,
  SearchFinanceEntriesInput,
} from '@etiquetachef/types';
import { FinanceEntryId } from '@etiquetachef/types';
import { subscribeFinanceAccounts } from '@/lib/firestoreFinance';
import { deleteFinanceEntryCallable, listFinanceTagsCallable, wasReportedByCallable } from '@/lib/callables';
import { toast } from '@/lib/notifications';
import { reportNonFatal, serializeError } from '@/lib/errorReport';
import { formatCurrencyCents } from '@/features/dashboard-finance/formatters';
import { useFinanceEntriesSearch } from '@/features/finance-entries/useFinanceEntriesSearch';
import { FinanceEntryEditorDialog } from '@/features/finance-entries/FinanceEntryEditorDialog';
import { serverDate } from '@etiquetachef/ui-shared/online';

const PAGE_SIZE = 25;

/** Dois dígitos com zero à esquerda. */
function pad(n: number): string {
  return String(n).padStart(2, '0');
}
/** `DD/MM/AAAA` no fuso do navegador. */
function formatDateMs(ms: number): string {
  const d = new Date(ms);
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${String(d.getFullYear())}`;
}
/** `AAAA-MM-DD` → início (ou fim, com `end`) do dia LOCAL em ms; vazio → undefined. */
function dayMs(value: string, end: boolean): number | undefined {
  if (value === '') return undefined;
  const [y, m, d] = value.split('-').map(Number);
  if (y === undefined || m === undefined || d === undefined) return undefined;
  return end
    ? new Date(y, m - 1, d, 23, 59, 59, 999).getTime()
    : new Date(y, m - 1, d, 0, 0, 0, 0).getTime();
}
/** 00:00 local de hoje — limite da regra "só edita do dia em diante" (#74). */
function startOfTodayLocalMs(): number {
  const d = serverDate();
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}
function dayStartMs(value: string): number | undefined {
  return dayMs(value, false);
}
function dayEndMs(value: string): number | undefined {
  return dayMs(value, true);
}

/**
 * Tela `/finance/entries`: lançamentos do razão interno — busca paginada com
 * filtros, totais por moeda, criar, editar e excluir.
 *
 * Origem: #74 — 2026-06-21 20:47 +0200 (tela de Lançamentos do Financeiro
 * interno).
 *
 * Por quê: a busca é no servidor (callable) e não por listener porque
 * filtra por texto, tag e período com paginação e totais da busca inteira —
 * nada disso cabe numa query Firestore sem um índice por combinação.
 *
 * Contrato: `useFinanceEntriesSearch` → callable `searchFinanceEntries`
 * (leitura, shim `requireSuperAdmin`), 25 por página, filtros `accountId`,
 * `type` (IN | OUT), `tag`, `dateFrom`/`dateTo` (início/fim do dia LOCAL)
 * e `text`; mudar filtro volta à página 1. `listFinanceTags` alimenta o
 * filtro e o autocomplete (falha degrada para "sem sugestões"). Mutações
 * com capability `manageFinance` (ROOT/FINANCE, `docs/permissions.md`):
 * `createFinanceEntry`/`updateFinanceEntry` no `FinanceEntryEditorDialog`
 * e `deleteFinanceEntry` aqui (confirmação em modal). Cada save/delete
 * incrementa `refreshNonce` e refaz busca e tags.
 *
 * Usado por: `routes/router.tsx` (rota `finance/entries`, dentro do
 * `RequireSuperAdmin`), menu Financeiro → "Lançamentos" (`AdminLayout`).
 * Só `apps/admin` (web). Spec: `docs/screens/finance-entries-admin.md`.
 *
 * Arquitetura: regra `match /finance_entries/{entryId}` (Lê: super-admin,
 * mas nenhum cliente lê direto; Escreve: só servidor). Regra de dia (#74):
 * só edita/exclui lançamento do dia atual ou futuro — a tela desabilita
 * pelo início do dia local, e o servidor recusa com `failed-precondition`
 * (`isPastDayEntry`, corte no fuso da conta, #105).
 *
 * Dados: `finance_entries/{id}` (`FinanceEntrySchema`):
 *   { "id": "ent-1", "accountId": "acc-1", "type": "OUT",
 *     "amountCents": 4590,          // CENTAVOS, sempre > 0; sinal = type
 *     "currency": "EUR",            // denormalizada da conta
 *     "date": 1780056000000,        // ms epoch ao MEIO-DIA UTC do dia
 *     "description": "Hospedagem", "tags": ["infra"],
 *     "source": "MANUAL", "importId": null }
 * Totais da busca por moeda (`inCents`/`outCents`, saldo = in − out).
 *
 * Falhas conhecidas:
 *  - 2026-07-02 (#105): o admin mandava meio-dia LOCAL; o corte de dia e o
 *    dedupe do import não casavam. Hoje o editor grava meio-dia UTC.
 *
 * Issues: #74 (origem, regra de dia), #77 (tags), #105 (fuso), #1021 (erro no modal).
 *
 * Histórico:
 *  - 2026-10-10 14:53 +0200 — (#798) cabeçalho padrão (#796).
 *  - 2026-10-25 15:00 +0100 — (#1021) falha da exclusão em `Alert` dentro do
 *    modal, que fica aberto (o toast ficava atrás do overlay, mesmo achado do
 *    CRM, #174), e report no PostHog (era só `console.error`).
 */
export function FinanceEntriesPage(): React.JSX.Element {
  const { t } = useTranslation();
  const [accounts, setAccounts] = useState<FinanceAccount[]>([]);
  const [knownTags, setKnownTags] = useState<string[]>([]);
  const [accountId, setAccountId] = useState('');
  const [type, setType] = useState('');
  const [tag, setTag] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [text, setText] = useState('');
  const [page, setPage] = useState(1);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<FinanceEntry | null>(null);
  const [deleting, setDeleting] = useState<FinanceEntry | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  // #1021 (2026-10-25 15:00 +0100): falha da exclusão, mostrada dentro do modal.
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    const unsubA = subscribeFinanceAccounts((r) => {
      if (r.kind === 'READY') setAccounts(r.accounts);
    });
    return (): void => {
      unsubA();
    };
  }, []);

  // Tags já usadas — alimentam o autocomplete do editor e o filtro de tag
  // (#77). Recarrega após cada save/delete (refreshNonce) para captar tags
  // novas. Falha silenciosa: degrada para "sem sugestões".
  useEffect(() => {
    let alive = true;
    listFinanceTagsCallable({})
      .then((r) => {
        if (alive) setKnownTags(r.tags);
      })
      .catch((err: unknown) => {
        console.error('listFinanceTags failed', err);
      });
    return (): void => {
      alive = false;
    };
  }, [refreshNonce]);

  const input: SearchFinanceEntriesInput = useMemo(
    () => ({
      ...(accountId !== '' ? { accountId } : {}),
      ...(type !== '' ? { type: type as 'IN' | 'OUT' } : {}),
      ...(tag !== '' ? { tag } : {}),
      ...(dayStartMs(dateFrom) !== undefined ? { dateFrom: dayStartMs(dateFrom) } : {}),
      ...(dayEndMs(dateTo) !== undefined ? { dateTo: dayEndMs(dateTo) } : {}),
      ...(text.trim() !== '' ? { text: text.trim() } : {}),
      page,
      pageSize: PAGE_SIZE,
    }),
    [accountId, type, tag, dateFrom, dateTo, text, page],
  );

  const state = useFinanceEntriesSearch(input, refreshNonce);
  const result = state.kind === 'READY' ? state.result : null;

  const accountNameById = useMemo(
    () => new Map(accounts.map((a) => [a.id, a.name])),
    [accounts],
  );

  // Reseta a página ao mudar filtros.
  useEffect(() => {
    setPage(1);
  }, [accountId, type, tag, dateFrom, dateTo, text]);

  /** Força nova busca e recarga das tags. */
  function refresh(): void {
    setRefreshNonce((n) => n + 1);
  }

  /**
   * Exclui pela callable `deleteFinanceEntry`; sucesso fecha o modal e recarrega.
   * Falha mantém o modal aberto com o `Alert` de erro dentro dele (#1021).
   */
  async function confirmDelete(): Promise<void> {
    if (!deleting) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await deleteFinanceEntryCallable({ entryId: FinanceEntryId(deleting.id) });
      toast.success(t('admin.finance.entries.success.deleted'));
      setDeleting(null);
      refresh();
    } catch (err) {
      console.error('deleteFinanceEntry failed', err);
      // Auditoria 2026-10-25 15:00 +0100 (#1021): era `toast.error`, atrás do overlay deste Modal, que
      // não fecha no erro (mesmo achado do CRM, #174); e o `console.error` sozinho não chega a nós.
      // 2ª auditoria (2026-10-25 15:52 +0100, #1021): falha da callable já sai como `callable_failed`
      // pelo wrapper; aqui só o que ele não vê (Zod do input, offline) — senão eram 2 eventos.
      if (!wasReportedByCallable(err)) {
        reportNonFatal('admin_finance_entry_delete_failed', serializeError(err));
      }
      setDeleteError(t('admin.finance.entries.errors.deleteFailed'));
    } finally {
      setDeleteBusy(false);
    }
  }

  const accountFilterOptions: SelectProps.Option[] = [
    { value: '', label: t('admin.finance.entries.filters.allAccounts') },
    ...accounts.map((a) => ({ value: a.id, label: a.name })),
  ];
  const typeFilterOptions: SelectProps.Option[] = [
    { value: '', label: t('admin.finance.entries.filters.allTypes') },
    { value: 'IN', label: t('admin.finance.entries.type.IN') },
    { value: 'OUT', label: t('admin.finance.entries.type.OUT') },
  ];
  // Une as tags do catálogo (#77) com as da janela atual, para não perder a
  // tag selecionada caso o cap de varredura a tenha deixado de fora.
  const tagFilterValues = Array.from(
    new Set([...knownTags, ...(result?.allTags ?? [])]),
  ).sort((a, b) => a.localeCompare(b));
  const tagFilterOptions: SelectProps.Option[] = [
    { value: '', label: t('admin.finance.entries.filters.allTags') },
    ...tagFilterValues.map((tg) => ({ value: tg, label: tg })),
  ];

  const totalsEntries = result ? Object.entries(result.totalsByCurrency) : [];
  // Regra (#74): só edita/exclui lançamento do mesmo dia ou futuro.
  const todayStart = startOfTodayLocalMs();

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description={t('admin.finance.entries.subtitle')}
          actions={
            <Button
              variant="primary"
              onClick={(): void => {
                setCreating(true);
              }}
            >
              {t('admin.finance.entries.create')}
            </Button>
          }
        >
          {t('admin.finance.entries.title')}
        </Header>
      }
    >
      <SpaceBetween size="m">
        {totalsEntries.length > 0 && (
          <SpaceBetween size="m">
            {totalsEntries.map(([currency, totals]) => {
              const net = totals.inCents - totals.outCents;
              return (
                <Container key={currency} header={<Header variant="h3">{currency}</Header>}>
                  <ColumnLayout columns={3} variant="text-grid">
                    <div>
                      <Box variant="awsui-key-label">{t('admin.finance.entries.kpi.in')}</Box>
                      <Box variant="awsui-value-large" color="text-status-success">
                        {formatCurrencyCents(totals.inCents, currency as never)}
                      </Box>
                    </div>
                    <div>
                      <Box variant="awsui-key-label">{t('admin.finance.entries.kpi.out')}</Box>
                      <Box variant="awsui-value-large" color="text-status-error">
                        {formatCurrencyCents(totals.outCents, currency as never)}
                      </Box>
                    </div>
                    <div>
                      <Box variant="awsui-key-label">{t('admin.finance.entries.kpi.net')}</Box>
                      <Box
                        variant="awsui-value-large"
                        color={net < 0 ? 'text-status-error' : 'text-status-success'}
                      >
                        {formatCurrencyCents(net, currency as never)}
                      </Box>
                    </div>
                  </ColumnLayout>
                </Container>
              );
            })}
          </SpaceBetween>
        )}

        {state.kind === 'ERROR' && <Alert type="error">{t('admin.finance.entries.errorLoad')}</Alert>}
        {result?.capped === true && (
          <Alert type="warning">{t('admin.finance.entries.capped')}</Alert>
        )}

        <Table<FinanceEntry>
          items={result?.items ?? []}
          loading={state.kind === 'LOADING'}
          trackBy="id"
          variant="container"
          resizableColumns
          stickyHeader
          filter={
            <SpaceBetween size="xs" direction="horizontal">
              <Select
                selectedOption={accountFilterOptions.find((o) => o.value === accountId) ?? null}
                options={accountFilterOptions}
                onChange={({ detail }): void => {
                  setAccountId(detail.selectedOption.value ?? '');
                }}
              />
              <Select
                selectedOption={typeFilterOptions.find((o) => o.value === type) ?? null}
                options={typeFilterOptions}
                onChange={({ detail }): void => {
                  setType(detail.selectedOption.value ?? '');
                }}
              />
              <Select
                selectedOption={tagFilterOptions.find((o) => o.value === tag) ?? null}
                options={tagFilterOptions}
                onChange={({ detail }): void => {
                  setTag(detail.selectedOption.value ?? '');
                }}
              />
              <DatePicker
                value={dateFrom}
                onChange={({ detail }): void => {
                  setDateFrom(detail.value);
                }}
                placeholder={t('admin.finance.entries.filters.from')}
              />
              <DatePicker
                value={dateTo}
                onChange={({ detail }): void => {
                  setDateTo(detail.value);
                }}
                placeholder={t('admin.finance.entries.filters.to')}
              />
              <Input
                type="search"
                value={text}
                placeholder={t('admin.finance.entries.filters.searchPlaceholder')}
                onChange={({ detail }): void => {
                  setText(detail.value);
                }}
              />
            </SpaceBetween>
          }
          pagination={
            <Pagination
              currentPageIndex={page}
              pagesCount={Math.max(1, Math.ceil((result?.total ?? 0) / PAGE_SIZE))}
              onChange={({ detail }): void => {
                setPage(detail.currentPageIndex);
              }}
            />
          }
          empty={
            <Box textAlign="center" color="text-status-inactive" padding="m">
              {t('admin.finance.entries.empty')}
            </Box>
          }
          columnDefinitions={[
            {
              id: 'date',
              header: t('admin.finance.entries.columns.date'),
              cell: (e): React.ReactNode => formatDateMs(e.date),
            },
            {
              id: 'account',
              header: t('admin.finance.entries.columns.account'),
              cell: (e): React.ReactNode => accountNameById.get(e.accountId) ?? e.accountId,
            },
            {
              id: 'description',
              header: t('admin.finance.entries.columns.description'),
              cell: (e): React.ReactNode => e.description,
            },
            {
              id: 'tags',
              header: t('admin.finance.entries.columns.tags'),
              cell: (e): React.ReactNode =>
                e.tags.length === 0 ? '—' : (
                  <SpaceBetween size="xxs" direction="horizontal">
                    {e.tags.map((tg) => (
                      <Badge key={tg}>{tg}</Badge>
                    ))}
                  </SpaceBetween>
                ),
            },
            {
              id: 'amount',
              header: t('admin.finance.entries.columns.amount'),
              cell: (e): React.ReactNode => (
                <Box
                  textAlign="right"
                  color={e.type === 'OUT' ? 'text-status-error' : 'text-status-success'}
                >
                  {e.type === 'OUT' ? '-' : ''}
                  {formatCurrencyCents(e.amountCents, e.currency)}
                </Box>
              ),
            },
            {
              id: 'actions',
              header: t('admin.finance.entries.columns.actions'),
              minWidth: 90,
              cell: (e): React.ReactNode => {
                const mutable = e.date >= todayStart;
                return (
                  <ButtonDropdown
                    variant="icon"
                    ariaLabel={t('admin.finance.actions.menu')}
                    expandToViewport
                    items={[
                      {
                        id: 'edit',
                        text: t('admin.finance.actions.edit'),
                        disabled: !mutable,
                        disabledReason: t('admin.finance.entries.lockedHint'),
                      },
                      {
                        id: 'delete',
                        text: t('admin.finance.actions.delete'),
                        disabled: !mutable,
                        disabledReason: t('admin.finance.entries.lockedHint'),
                      },
                    ]}
                    onItemClick={({ detail }): void => {
                      if (detail.id === 'edit') setEditing(e);
                      else if (detail.id === 'delete') {
                        setDeleteError(null);
                        setDeleting(e);
                      }
                    }}
                  />
                );
              },
            },
          ]}
        />
      </SpaceBetween>

      <FinanceEntryEditorDialog
        open={creating || editing !== null}
        mode={editing ? 'edit' : 'create'}
        entry={editing}
        accounts={accounts}
        knownTags={knownTags}
        onClose={(): void => {
          setCreating(false);
          setEditing(null);
          refresh();
        }}
      />

      <Modal
        visible={deleting !== null}
        // #1021 (2ª auditoria 2026-10-25 15:52 +0100): com a ação em voo, Esc/X/clique fora
        // fechavam o modal e a falha caía no `Alert` dele, já fechado — ninguém a via.
        onDismiss={(): void => { if (!(deleteBusy)) { setDeleteError(null); setDeleting(null); } }}
        header={t('admin.finance.entries.deleteTitle')}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                onClick={(): void => {
                  setDeleteError(null);
                  setDeleting(null);
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                loading={deleteBusy}
                onClick={(): void => {
                  void confirmDelete();
                }}
              >
                {t('admin.finance.actions.delete')}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <Box variant="p">{t('admin.finance.entries.deleteConfirm')}</Box>
          {/* Auditoria 2026-10-25 15:00 +0100 (#1021): erro dentro do modal, junto do footer (#174). */}
          {deleteError !== null && <Alert type="error">{deleteError}</Alert>}
        </SpaceBetween>
      </Modal>
    </ContentLayout>
  );
}
